import { useDispatch, useSelector } from "react-redux";
import ClueButton from "../ClueButton";
import ImageLetterContainer from "../ImageLetterContainer";
import classes from './GameContainer.module.scss';
import { useEffect, useState } from "react";
import { createLetter, getFirstNotDisabledWordIndex, getIndexRevealLetter, isAllInputsFilled, recoverOriginalAnswer, removeClueLetters, standardizeAnswer, standardizeWithoutCorrectWords } from "../../utils/functions";
import { VARIANT_INPUT } from "../../utils/constants";
import CluesProvider from '../CluesProvider';
import { finishGame, saveAnswer, setAnimating, skipQuestion } from "@educaplay/store/slices/gameSlice";
import PillMessage from "../PillMessage";
import { useGameSounds } from "@educaplay/core";
import { useAnimate } from "framer-motion";
import { useImperativeHandle } from "react";
import { useRef } from "react";

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const DURATION_CLUE_ANIMATION = 800;
const DURATION_ERROR_ANIMATION = 1200;
const DELAY_ERROR_ANIMATION = 300;


function GameContainer(props) {

    const { onAnswer, ref } = props;
    const questions = useSelector(state => state.game.questions);
    const numQuestions = questions.length;
    const currentIndex = useSelector(state => state.game.currentIndex);
    const currentQuestion = questions[currentIndex];
    const [lettersAnswer, setLettersAnswer] = useState(standardizeAnswer(currentQuestion.correctAnswers[0]));
    const [showMessage, setShowMessage] = useState(false);
    const [variant, setVariant] = useState();
    const [pointsLose, setPointsLose] = useState(0);
    const dispatch = useDispatch();
    const isAnimating = useSelector(state => state.game.isAnimating);
    const gameSound = useGameSounds();
    const [imageRef, animateImage] = useAnimate();
    const [inputAnimationRef, animateInput] = useAnimate();
    const [clueButtonRef, animateClueButton] = useAnimate();
    const inputRef = useRef();
    const pixelateImage = useRef(true);


    useImperativeHandle(ref, () => ({
        animateEntry: async () => {
            if (imageRef.current) await animateImage(imageRef.current, { opacity: 1, scale: 1 }, { delay: 0.4, duration: .6, type: "spring" });
            if (clueButtonRef.current) animateClueButton(clueButtonRef.current, { x: 0, opacity: 1 }, { duration: .3, type: "spring", bounce: 0.5 })
            animateInput(inputAnimationRef.current, { opacity: 1, scale: 1 }, { duration: .3, type: "spring" });
        },

        animateExit: async () => {
            if (imageRef.current) animateImage(imageRef.current, { opacity: 0, scale: .2 }, { duration: .6, type: "spring" });
            if (clueButtonRef.current) animateClueButton(clueButtonRef.current, { x: "100%", opacity: 0 }, { duration: .3, type: "spring", bounce: 0.5 });
            animateInput(inputAnimationRef.current, { opacity: 0, scale: .2 }, { duration: .3, type: "spring" });
        }
    }));



    useEffect(() => {
        setLettersAnswer(standardizeAnswer(currentQuestion.correctAnswers[0]));
        setShowMessage(false);
        setVariant();
        setPointsLose(0);
        pixelateImage.current = true;

        if (currentIndex > 0) {
            const nextQuestionAnimation = async () => {
                await ref.current.animateExit();
                ref.current.animateEntry();
            }

            nextQuestionAnimation();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQuestion.id]);


    const checkAnswer = async (newLetters) => {
        if (isAllInputsFilled(newLetters)) {
            const originalAnswer = recoverOriginalAnswer(newLetters);
            const answer = {
                answer: originalAnswer,
                pointsLose
            }

            onAnswer(answer, { showFeedback: true, feedbackDuration: 300, delayLiveAnimation: 800 });

            const hasAcert = currentQuestion.checkAnswer(answer);

            if (!hasAcert) {
                await wait(DELAY_ERROR_ANIMATION);

                setVariant(VARIANT_INPUT.INCORRECT);
                const answerActualized = standardizeWithoutCorrectWords(newLetters, currentQuestion.correctAnswers[0]);
                await wait(DURATION_ERROR_ANIMATION);
                setVariant();
                setLettersAnswer(answerActualized);
                inputRef.current.focusWord(getFirstNotDisabledWordIndex(answerActualized));

            } else {
                setVariant(VARIANT_INPUT.CORRECT);
                gameSound.play("correct");
                setShowMessage(true);
                pixelateImage.current = false;
                await wait(900);
                changeScreenAnimation();
            }
        }
    }

    const changeScreenAnimation = async () => {
        ref.current.animateExit();
        const hasMoreQuestions = currentQuestion.id < numQuestions - 1;
        if (hasMoreQuestions) {
            await wait(400);
            ref.current.animateEntry();
        }
    }

    const revealLetter = async () => {
        if (isAnimating) return;
        dispatch(setAnimating(true));
        const { wordIndex, letterIndex } = getIndexRevealLetter(lettersAnswer, currentQuestion.correctAnswers[0]);
        const actualizedAnswer = [...lettersAnswer];
        const letters = standardizeAnswer(currentQuestion.correctAnswers[0], { emptyLetters: false });
        actualizedAnswer[wordIndex][letterIndex] = createLetter(letters[wordIndex][letterIndex].value, {
            disabled: true,
            isClue: true,
        });

        setLettersAnswer(actualizedAnswer);

        await wait(DURATION_CLUE_ANIMATION);

        const actualizedAnswerWithoutClass = removeClueLetters(lettersAnswer);
        setLettersAnswer(actualizedAnswerWithoutClass);
        dispatch(setAnimating(false));

        await wait(100);

        if (isAllInputsFilled(actualizedAnswerWithoutClass)) {
            checkAnswer(actualizedAnswerWithoutClass);
            return;
        }
        inputRef.current.focusFirstLetterEmpty();
    }

    const onChangeHandler = (newLetters) => {
        setLettersAnswer(newLetters);
        checkAnswer(newLetters);
    }

    const handleClickPassButton = async () => {
        const answer = {
            answer: "",
            pointsLose
        }
        dispatch(saveAnswer(answer));
        const action = currentIndex === questions.length - 1 ? finishGame : skipQuestion;
        dispatch(action());
        changeScreenAnimation();
    }

    return (
        <CluesProvider lettersAnswer={lettersAnswer} revealLetter={revealLetter} inputRef={inputRef}>
            <div className={classes.gamecontainer}>
                <ImageLetterContainer
                    key={currentQuestion.id}
                    pixelateImage={pixelateImage.current}
                    onChange={onChangeHandler}
                    inputValue={lettersAnswer}
                    stateVariant={variant}
                    inputRef={inputRef}
                    imageRef={imageRef}
                    inputAnimationRef={inputAnimationRef}
                    changePointsLose={setPointsLose}
                    onSkip={handleClickPassButton}
                />
                <PillMessage open={showMessage} />
            </div>
            <ClueButton clueButtonRef={clueButtonRef} />
        </CluesProvider>
    )
}
export default GameContainer;